import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { useAppDispatch } from "../../../redux/hooks/useTypedSelector";
import { resetAuth, signInAction } from "../../../redux/slice/auth/authSlice";
import {
  SignIn as ISignIn,
  SignInValidationSchema,
} from "../../../apps/auth/schema";
import {
  Field,
  FormAlert,
  GoogleIcon,
  PasswordField,
  SubmitButton,
} from "./fields";
import { authErrorMessage } from "./authErrorMessage";
import { AuthView } from "./AuthModalContext";

export const SignInForm = ({
  onSwitch,
}: {
  onSwitch: (view: AuthView) => void;
}) => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(SignInValidationSchema),
    defaultValues: { email: "", password: "" },
  });

  const signIn = async (data: ISignIn) => {
    setError("");
    const res = await dispatch(signInAction(data));
    const payload = res.payload as { user?: unknown } | undefined;
    if (signInAction.fulfilled.match(res) && payload?.user) {
      window.location.href = "/dashboard";
      return;
    }
    setError(
      authErrorMessage(
        res.payload,
        "We couldn't sign you in. Please check your email and password.",
      ),
    );
    dispatch(resetAuth());
  };

  const handleGoogleSignIn = useGoogleLogin({
    onSuccess: async (response) => {
      setGoogleLoading(true);
      try {
        const res = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${response.access_token}` } },
        );
        await signIn({
          email: res.data.email,
          firstName: res.data.name.split(" ")[0],
          lastName: res.data.name.split(" ")[1],
          password: "",
          isGoogleLogIn: true,
        } as ISignIn);
      } catch {
        setError("Google sign-in failed. Please try again.");
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => setError("Google sign-in was cancelled or failed."),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="font-headline-md text-headline-md text-on-surface">
          Welcome back
        </h2>
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Sign in to continue your CELPIP preparation.
        </p>
      </div>

      {error && <FormAlert tone="error">{error}</FormAlert>}

      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(signIn)}
        noValidate
      >
        <Field
          id="signin-email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register("email")}
        />
        <PasswordField
          id="signin-password"
          label="Password"
          autoComplete="current-password"
          placeholder="Your password"
          error={errors.password?.message}
          trailing={
            <button
              type="button"
              onClick={() => onSwitch("forgot")}
              className="font-label-md text-xs text-primary hover:underline"
            >
              Forgot password?
            </button>
          }
          {...register("password")}
        />
        <SubmitButton loading={isSubmitting}>Sign In</SubmitButton>
      </form>

      <div className="flex items-center gap-3 text-xs text-on-surface-variant">
        <span className="h-px flex-1 bg-outline-variant/60" />
        or
        <span className="h-px flex-1 bg-outline-variant/60" />
      </div>

      <button
        type="button"
        onClick={() => handleGoogleSignIn()}
        disabled={googleLoading}
        className="w-full h-12 inline-flex items-center justify-center gap-3 rounded-xl bg-surface-container-lowest border border-outline-variant text-on-surface font-label-md text-label-md hover:bg-surface-container-low transition-colors disabled:opacity-70"
      >
        <GoogleIcon />
        Continue with Google
      </button>

      <p className="font-body-sm text-body-sm text-on-surface-variant text-center">
        Don't have an account?{" "}
        <button
          type="button"
          onClick={() => onSwitch("signup")}
          className="font-label-md text-label-md text-primary hover:underline"
        >
          Create one free
        </button>
      </p>
    </div>
  );
};
